import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';
import { CarIcon, ChevronRightIcon } from 'assets/icons';
import { VehicleCountry } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { fetchKnownVehicle } from 'domains/catalog/fetchKnownVehicle';
import { ScalableImage } from 'UI';
import VehicleFlag from 'UI/Icon/VehicleFlag';
import { formatVehicleBrand } from 'utils/mappers';
import {
  SArrow,
  SDetailLabel,
  SDetailRow,
  SFlagColumn,
  SRow,
  SThumbnailWrapper,
  SVehicleDetails,
  SVehicleName,
  SVrn,
  SVrnColumn,
} from './SearchHistoryRow.styled';

export function getVehicleName(vehicle: VehicleDetail): string {
  if (vehicle.catalogSource === 'DATAHUB') {
    return (vehicle.dataHubVehicle?.name ?? '').toUpperCase();
  }
  return `${formatVehicleBrand(vehicle.vehicleBrand)} ${vehicle.model ?? ''}`.trim().toUpperCase();
}

export function getEngineDisplay(vehicle: VehicleDetail): string | undefined {
  if (vehicle.catalogSource === 'DATAHUB') {
    return vehicle.dataHubVehicle?.engine || undefined;
  }
  return vehicle.iamVehicle?.engineCode || undefined;
}

interface SearchHistoryRowProps {
  vehicle: VehicleDetail;
  isActive?: boolean;
}

export function SearchHistoryRow({ vehicle, isActive }: Readonly<SearchHistoryRowProps>) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const handleSelect = useCallback(() => {
    const params = new URLSearchParams(search);
    if (vehicle.country) {
      params.set(VehicleCountry, vehicle.country);
    }
    fetchKnownVehicle(dispatch, vehicle);
    history.push(
      `${getCatalogSourceUrl(vehicle.catalogSource)}/${vehicle.vehicleKey}${ROUTER_CATALOG_VEHICLE}?${params}`,
    );
  }, [vehicle, history, search, dispatch]);

  const vehicleName = getVehicleName(vehicle);
  const engineDisplay = getEngineDisplay(vehicle);
  const hasVrn = Boolean(vehicle.vrn);
  const hasVin = Boolean(vehicle.vin);
  const hasEngine = Boolean(engineDisplay);
  const showDetailRow = hasVin || hasEngine;

  const detailParts: Array<{ label: string; value: string }> = [];
  if (hasVin) {
    detailParts.push({ label: 'VIN:', value: ` ${vehicle.vin}` });
  }
  if (hasEngine) {
    detailParts.push({ label: 'Engine:', value: ` ${engineDisplay}` });
  }

  return (
    <SRow
      onClick={handleSelect}
      data-testid={'search-history-row'}
      $isActive={isActive}
      data-active={isActive ?? false}
    >
      <SThumbnailWrapper>
        <ScalableImage
          width={50}
          height={50}
          imageUrl={vehicle.imageUrl}
          catalogSource={vehicle.catalogSource}
          fallbackComponent={<CarIcon width={30} height={30} />}
          alt={vehicleName}
          cursor={'auto'}
        />
      </SThumbnailWrapper>
      <SVehicleDetails>
        <SVehicleName>{vehicleName}</SVehicleName>
        {showDetailRow && (
          <SDetailRow>
            {detailParts.map((part, i) => (
              <span key={part.label}>
                {i > 0 && '   '}
                <SDetailLabel>{part.label}</SDetailLabel>
                {part.value}
              </span>
            ))}
          </SDetailRow>
        )}
      </SVehicleDetails>
      <SFlagColumn>
        <VehicleFlag vehicle={vehicle} />
      </SFlagColumn>
      <SVrnColumn>{hasVrn && <SVrn>{vehicle.vrn}</SVrn>}</SVrnColumn>
      <SArrow>
        <ChevronRightIcon />
      </SArrow>
    </SRow>
  );
}
